import { useState, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Modal, Button, Divider } from '@ets/design-system';

import { useDispatch, useSelector } from '_common/hooks';
import EditorManager from 'Editor/services/EditorManager';
import { closeModal } from '_common/modals/ModalsSlice';

import { MMLHelper } from '_common/utils/MMLHelper';
import EquationsContext from './EquationsContext';
import Preview from './Preview';
import { Symbol, Structure } from './ToolbarElements';
import ToolbarGroup from './ToolbarGroup/ToolbarGroup';

import { Textarea, type TextareaAPI, type TextareaProps } from '_common/lib/components';

const EquationsModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const isOpen = useSelector((state) => state.modals.open.EquationsModal);
  const isEdit = useSelector((state) => state.modals.EquationsModal.isEdit);
  const equation = useSelector((state) => state.modals.EquationsModal.equation);

  const [text, setText] = useState(equation ? equation.tex : '');
  const textareaRef = useRef<TextareaAPI>(null);
  const [isValid, setIsValid] = useState(true);

  const onElementInsertion = (
    element: Symbol['elements'][number] | Structure['elements'][number],
  ) => {
    const newText = element['latex'];
    // Add a space after inserted LaTeX to separate it from following content.
    const insertedText = `${newText} `;
    if (textareaRef.current) {
      const { from, to } = textareaRef.current.getSelection();

      // getSelection returns 1-based indexes
      const start = from - 1;
      const end = to - 1;

      // substring is 0-based
      const before = text.substring(0, start);
      const after = text.substring(end, text.length);
      const newValue = before + insertedText + after;
      setText(newValue);
      textareaRef.current.setValue(newValue);
      textareaRef.current.focus();

      setTimeout(() => {
        if (textareaRef.current && insertedText) {
          // setSelection expects 1-based indexes
          textareaRef.current.setSelection({
            // New selection is at the end of the inserted text
            from: end - (end - start) + insertedText.length + 1,
            to: end - (end - start) + insertedText.length + 1,
          });
        }
      }, 0);
    }
  };
  const close = () => {
    dispatch(closeModal('EquationsModal'));
    EditorManager.getInstance().restore();
  };

  const handleButtonClick = () => {
    const mml = MMLHelper.latexToMML(text);

    if (isEdit) {
      EditorManager.getInstance().updateEquation(equation.blockId, equation.elementId, mml);
    } else {
      EditorManager.getInstance().insertEquation(mml);
    }
    close();
  };

  const handleTextChange: TextareaProps['onChange'] = (e) => {
    const target = e.target as HTMLTextAreaElement;
    setText(target.value);
  };

  return (
    <Modal width="127rem" open={!!isOpen} close={close} testId="equations">
      <Modal.Header>
        <FormattedMessage id="EQUATION_EDITOR" />
      </Modal.Header>
      <Modal.Body>
        <EquationsContext.Provider value={{ onElementInsertion, setIsValid }}>
          <div className="mb-16 flex h-28">
            <ToolbarGroup type="structures" />
            <Divider vertical className="mx-[3.4rem]" />
            <ToolbarGroup type="symbols" />
          </div>
          <Preview value={text} isValid={isValid} equation={equation} />
          <Textarea
            plainText
            name="equations"
            type="text"
            ref={textareaRef}
            initialContent={text}
            onChange={handleTextChange}
            size="l"
            placeholder={intl.formatMessage({ id: 'INSERT_YOUR_EQUATION_HERE' })}
            hasError={!isValid && !!text}
            testId="equations"
            className="mb-2"
          />
        </EquationsContext.Provider>
      </Modal.Body>
      <Modal.Footer>
        <Button size="m" variant="standard" onClick={close} testId="equations-cancel">
          <FormattedMessage id="global.close" />
        </Button>
        <Button
          size="m"
          variant="primary"
          className="ml-4"
          onClick={handleButtonClick}
          disabled={!isValid || text.length === 0}
          testId="equations-submit"
        >
          <FormattedMessage id={isEdit ? 'UPDATE' : 'global.insert'} />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EquationsModal;
